import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'
import SearchableSelect from '../SearchableSelect'

interface ContainersTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

interface ContainerType {
  id: string
  code: string
  boxtop_name: string | null
}

export interface JobContainer {
  id?: string
  container_type_id?: string | null
  container_no?: string | null
  seal_no?: string | null
  no_of_pcs?: number | null
  gross_weight?: number | null
  said_to_contain?: string | null
  cube?: number | null
  release_ref?: string | null
  notes?: string | null
}

// ISO 6346: 4 letters (owner code + category ID) + 7 digits (serial + check digit)
export const ISO_6346_RE = /^[A-Z]{4}\d{7}$/

export function isIsoContainerNumber(value: string | null | undefined): boolean {
  return !!value && ISO_6346_RE.test(value.trim().toUpperCase())
}

const isRoadMode = (mode: string | null | undefined) => mode === 'ROAD' || mode === 'TRUCK'

export default function ContainersTab({ jobData, setJobData, readOnly = false }: ContainersTabProps) {
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([])
  const road = isRoadMode(jobData.transport_mode)

  useEffect(() => {
    supabase.from('container_types').select('id, code, boxtop_name').eq('is_active', true).order('display_order')
      .then(({ data }) => setContainerTypes(data || []))
  }, [])

  if (jobData.transport_mode !== 'SEA' && !road) return null

  const containers: JobContainer[] = jobData.containers || []

  const addRow = () => {
    setJobData({ ...jobData, containers: [...containers, { id: `new-${Date.now()}` }] })
  }

  const removeRow = (index: number) => {
    setJobData({ ...jobData, containers: containers.filter((_, i) => i !== index) })
  }

  const updateRow = (index: number, field: keyof JobContainer, value: any) => {
    const updated = [...containers]
    updated[index] = { ...updated[index], [field]: value }
    setJobData({ ...jobData, containers: updated })
  }

  if (road) {
    return (
      <div className="space-y-3">
        <h3 className="font-medium text-gray-900 dark:text-white">Vehicle Plate</h3>
        {containers.map((c, idx) => (
          <div key={c.id ?? idx} className="flex items-center gap-2">
            <input type="text" value={c.container_no || ''}
              onChange={e => updateRow(idx, 'container_no', e.target.value.toUpperCase())}
              disabled={readOnly}
              placeholder="e.g. AB12 CDE"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100" />
            {!readOnly && (
              <button onClick={() => removeRow(idx)} className="text-red-600 hover:text-red-900 dark:hover:text-red-400 text-sm">
                Remove
              </button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button onClick={addRow}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
            + Add Vehicle
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900 dark:text-white">Containers</h3>
        {!readOnly && (
          <button onClick={addRow}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
            + Add Row
          </button>
        )}
      </div>

      {containers.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No containers added yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Container No</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Seal No</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Type</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Said to Contain</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Pieces</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Cargo Weight (kg)</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Cube (cbm)</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Release Ref</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Notes</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {containers.map((c, idx) => {
                const raw = c.container_no || ''
                const invalid = raw.length > 0 && !isIsoContainerNumber(raw)
                return (
                  <tr key={c.id ?? idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2">
                      <input type="text" value={raw}
                        onChange={e => updateRow(idx, 'container_no', e.target.value.toUpperCase())}
                        disabled={readOnly}
                        placeholder="ABCU1234567"
                        className={`w-32 px-2 py-1 border rounded dark:bg-gray-700 dark:text-white font-mono ${invalid ? 'border-amber-400' : 'border-gray-300 dark:border-gray-600'}`} />
                      {invalid && <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">Not ISO 6346 (AAAU1234567)</p>}
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.seal_no || ''}
                        onChange={e => updateRow(idx, 'seal_no', e.target.value)}
                        disabled={readOnly}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2 min-w-[160px]">
                      <SearchableSelect
                        options={containerTypes.map(t => ({ id: t.id, label: t.boxtop_name ? `${t.boxtop_name} (${t.code})` : t.code }))}
                        value={c.container_type_id || ''}
                        onChange={value => updateRow(idx, 'container_type_id', value)}
                        disabled={readOnly}
                        placeholder="Select type..."
                        allowCreate={false}
                        // Z43: this table sits in an overflow-x-auto wrapper, which clips the
                        // dropdown's normal absolutely-positioned list (looks like zero options
                        // even though containerTypes loaded fine) -- portal it past the clip.
                        usePortal
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.said_to_contain || ''}
                        onChange={e => updateRow(idx, 'said_to_contain', e.target.value)}
                        disabled={readOnly}
                        className="w-40 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="1" value={c.no_of_pcs ?? ''}
                        onChange={e => updateRow(idx, 'no_of_pcs', e.target.value ? parseInt(e.target.value, 10) : null)}
                        disabled={readOnly}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.001" value={c.gross_weight ?? ''}
                        onChange={e => updateRow(idx, 'gross_weight', e.target.value ? parseFloat(e.target.value) : null)}
                        disabled={readOnly}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.001" value={c.cube ?? ''}
                        onChange={e => updateRow(idx, 'cube', e.target.value ? parseFloat(e.target.value) : null)}
                        disabled={readOnly}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.release_ref || ''}
                        onChange={e => updateRow(idx, 'release_ref', e.target.value)}
                        disabled={readOnly}
                        className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.notes || ''}
                        onChange={e => updateRow(idx, 'notes', e.target.value)}
                        disabled={readOnly}
                        className="w-40 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white" />
                    </td>
                    <td className="px-3 py-2">
                      {!readOnly && (
                        <button onClick={() => removeRow(idx)} className="text-red-600 hover:text-red-900 dark:hover:text-red-400">
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
